import React, { createContext, startTransition, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { dict } from "./translations";

const LanguageContext = createContext({
  lang: "it",
  setLang: () => {},
  t: (key) => key,
});

// Walks a nested dictionary object following a dotted path. Returns `undefined`
// if any segment is missing. Pure module-level utility.
function getValueAtPath(obj, path) {
  return path.split(".").reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
}

// Replaces {var} placeholders in a translation string with the corresponding
// value from `vars`. Returns the original value unchanged if not a string.
function interpolatePlaceholders(val, vars) {
  if (typeof val !== "string" || !vars) return val;
  return Object.keys(vars).reduce((s, k) => s.replaceAll(`{${k}}`, vars[k]), val);
}

// Builds a translator bound to one language.
function createTranslator(lang) {
  const table = dict[lang] || dict.it;
  return (key, vars) => {
    const val = getValueAtPath(table, key);
    if (val === undefined) return key;
    return interpolatePlaceholders(val, vars);
  };
}

// One stable translator per supported language, created once at module load.
// Consumers therefore get a referentially-stable `t` for a given `lang`
// without any hook-level memoization (and without hook dependency pitfalls).
const TRANSLATORS = Object.freeze(
  Object.fromEntries(Object.keys(dict).map((lang) => [lang, createTranslator(lang)]))
);

function readStoredLang() {
  if (typeof window === "undefined") return "it";
  const stored = window.localStorage.getItem("aq_lang");
  return stored === "it" || stored === "en" ? stored : "it";
}

export function LanguageProvider({ children }) {
  // Always start from "it" (what the pre-rendered HTML was built with) so the
  // client hydrates the server markup 1:1; a stored "en" preference is applied
  // right after mount (one re-render, no hydration mismatch).
  const [lang, setLangState] = useState("it");
  const [hydrated, setHydrated] = useState(false);

  // Language changes are TRANSITIONS on purpose. Route pages are lazy() chunks
  // inside Suspense boundaries that hydrate AFTER the first paint; a sync
  // setState reaching a still-dehydrated boundary forces React to throw the
  // server HTML away (hydration mismatch "text"), while a transition lets
  // React hydrate the boundary with the server content first and apply the
  // new language right after (React 18+ selective hydration semantics).
  const setLang = useCallback((next) => {
    startTransition(() => setLangState(next));
  }, []);

  useEffect(() => {
    const stored = readStoredLang();
    if (stored !== "it") setLang(stored);
    setHydrated(true);
  }, [setLang]);

  useEffect(() => {
    if (typeof window !== "undefined" && hydrated) {
      window.localStorage.setItem("aq_lang", lang);
      document.documentElement.lang = lang;
    }
  }, [lang, hydrated]);

  const t = TRANSLATORS[lang] || TRANSLATORS.it;

  // Memoize the context value so consumers don't re-render unless lang changes.
  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  return useContext(LanguageContext);
}
